// alternative-dashboard/alternative-components/compactWidgetsCardDetails/AccountsWidget.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import type { AccountItem } from "../../types/compactWidgets";
import { t } from "i18next";
import { useQuery } from "@tanstack/react-query";
import { gxDashboardAccountsBalanceApi } from "@/app/apiConfig/services/customerAbstractApiLayer";

interface AccountsWidgetProps {
  title?: string;
}

const statusLabel = (status: string) => {
  switch (status) {
    case "active":
      return "Active";
    case "inactive":
      return "Inactive";
    case "dormant":
      return "Dormant";
    default:
      return status;
  }
};

const mapToAccountItems = (data: any): AccountItem[] => {
  if (!data) return [];

  const accounts = Array.isArray(data)
    ? data
    : (data.accountList ?? data.accounts ?? data.data ?? []);

  return accounts.map((item: any, index: number) => ({
    id: item.accountNo ?? `${index}`,
    bankCode: item.accountBankName ?? "",
    maskedAccountNo: item.accountNo ? `**** ${item.accountNo.slice(-4)}` : "",
    accountTypeLabel: item.accountType ?? "CASA",
    status: (item.status ?? "active").toLowerCase(),
    balance: item.availableBalance ?? 0,
    currencyCode: item.currencyCode ?? item.currency ?? "AED",
  }));
};

const ACCOUNTS_PER_SLIDE = 2;
const AUTO_PLAY_INTERVAL_MS = 4000;
const SWIPE_THRESHOLD = 40;

const AccountsWidget = ({ title }: AccountsWidgetProps) => {
  const {
    data: casaBalanceData,
    isLoading,
    error: casaBalanceApiError,
  } = useQuery({
    queryKey: ["dashboardAccountsBalance", "CASA"],
    queryFn: async () => {
      const response = await gxDashboardAccountsBalanceApi({
        pageNo: 0,
        pageSize: 0,
        filterBy2List: [
          {
            filterKey: "TYPE",
            filterStringValue: "CASA",
          },
        ],
      });

      if (response.status === 1) {
        return response.data;
      }

      throw new Error(response.errorMessage);
    },
  });

  useEffect(() => {
    if (casaBalanceApiError) {
      console.error(
        "AccountsWidget: gxDashboardAccountsBalanceApi failed",
        casaBalanceApiError,
      );
    }
  }, [casaBalanceApiError]);

  const accounts = mapToAccountItems(casaBalanceData);

  // Group accounts into slides of ACCOUNTS_PER_SLIDE
  const slides = useMemo(() => {
    const grouped: AccountItem[][] = [];
    for (let i = 0; i < accounts.length; i += ACCOUNTS_PER_SLIDE) {
      grouped.push(accounts.slice(i, i + ACCOUNTS_PER_SLIDE));
    }
    return grouped;
  }, [accounts]);

  const hasLoop = slides.length > 1;

  const extendedSlides = useMemo(() => {
    if (!hasLoop) return slides;
    return [slides[slides.length - 1], ...slides, slides[0]];
  }, [slides, hasLoop]);

  const [slideIndex, setSlideIndex] = useState(hasLoop ? 1 : 0);
  const [withTransition, setWithTransition] = useState(true);
  const slideIndexRef = useRef(slideIndex);
  const isAnimatingRef = useRef(false);
  const autoPlayTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dragStartXRef = useRef<number | null>(null);

  const setSlideIndexSynced = useCallback(
    (updater: number | ((prev: number) => number)) => {
      setSlideIndex((prev) => {
        const next =
          typeof updater === "function"
            ? (updater as (p: number) => number)(prev)
            : updater;
        slideIndexRef.current = next;
        return next;
      });
    },
    [],
  );


  useEffect(() => {
    isAnimatingRef.current = false;
    setWithTransition(false);
    setSlideIndexSynced(hasLoop ? 1 : 0);
  }, [slides.length, hasLoop]);


  useEffect(() => {
    if (withTransition) return;
    const raf = requestAnimationFrame(() => setWithTransition(true));
    return () => cancelAnimationFrame(raf);
  }, [withTransition]);


  const goToNext = useCallback(() => {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    setSlideIndexSynced((prev) => prev + 1);
  }, [setSlideIndexSynced]);


  const goToPrev = useCallback(() => {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    setSlideIndexSynced((prev) => prev - 1);
  }, [setSlideIndexSynced]);


  const goToSlide = useCallback(
    (dotIndex: number) => {
      if (isAnimatingRef.current) return;
      isAnimatingRef.current = true;
      setSlideIndexSynced(hasLoop ? dotIndex + 1 : dotIndex);
    },
    [hasLoop, setSlideIndexSynced],
  );



  const handleTransitionEnd = useCallback(() => {
    if (!hasLoop) {
      isAnimatingRef.current = false;
      return;
    }

    const lastExtendedIndex = extendedSlides.length - 1;

    if (slideIndexRef.current === lastExtendedIndex) {
      setWithTransition(false);
      setSlideIndexSynced(1);
    } else if (slideIndexRef.current === 0) {
      setWithTransition(false);
      setSlideIndexSynced(slides.length);
    }

    isAnimatingRef.current = false;
  }, [hasLoop, extendedSlides.length, slides.length, setSlideIndexSynced]);



  useEffect(() => {
    if (!hasLoop) return undefined;

    autoPlayTimerRef.current = setInterval(() => {
      goToNext();
    }, AUTO_PLAY_INTERVAL_MS);

    return () => {
      if (autoPlayTimerRef.current) clearInterval(autoPlayTimerRef.current);
    };
  }, [hasLoop, goToNext]);



  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!hasLoop) return;
    dragStartXRef.current = event.clientX;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartXRef.current === null) return;

    const deltaX = event.clientX - dragStartXRef.current;
    dragStartXRef.current = null;

    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;

    if (deltaX < 0) {
      goToNext();
    } else {
      goToPrev();
    }
  };

  const handlePointerCancel = () => {
    dragStartXRef.current = null;
  };

  const activeDotIndex = useMemo(() => {
    if (!hasLoop) return 0;
    if (slideIndex === 0) return slides.length - 1;
    if (slideIndex === extendedSlides.length - 1) return 0;
    return slideIndex - 1;
  }, [hasLoop, slideIndex, slides.length, extendedSlides.length]);


    console.log({
    isLoading,
    data: casaBalanceData,
  });


  if (isLoading) {
    return <div className="gx-compact-widget gx-compact-widget--skeleton" />;
  }


  return (
    <div className="gx-compact-widget">
      <h3 className="gx-compact-widget__title gx-p18-b">
        {title || t("accounts")}
      </h3>

      {accounts.length === 0 ? (
        <NoDataFound message="No accounts found" />
      ) : (
        <div className="gx-account-carousel">
          <div
            className="gx-account-carousel__viewport"
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
          >
            <div
              className="gx-account-carousel__track"
              style={{
                transform: `translateX(-${slideIndex * 100}%)`,
                transition: withTransition ? "transform 0.4s ease" : "none",
              }}
              onTransitionEnd={handleTransitionEnd}
            >
              {extendedSlides.map((slideAccounts, extIndex) => (
                <div
                  className="gx-account-carousel__slide"
                  key={`slide-${extIndex}`}
                >
                  {slideAccounts.map((account) => {
                    const formatted = gxCurrencyFormat(
                      account.balance,
                      account.currencyCode,
                      undefined,
                      { showSymbol: true },
                    );

                    return (
                      <div className="gx-account-row" key={account.id}>
                        <div className="gx-account-row__main">
                          <p className="gx-account-row__title gx-p14-b">
                            {account.bankCode} {account.maskedAccountNo}
                          </p>
                          <div className="gx-account-row__meta">
                            <span
                              className={`gx-status-pill gx-p10-sb gx-status-pill--${account.status}`}
                            >
                              {account.accountTypeLabel} &middot;{" "}
                              {statusLabel(account.status)}
                            </span>
                          </div>
                        </div>

                        <div className="gx-account-row__amount">
                          <span className="gx-compact-amount__symbol gx-p14-r">
                            {formatted.symbol}
                          </span>
                          <span className="gx-compact-amount__integer gx-p14-b">
                            {formatted.integer}
                          </span>
                          {formatted.decimal && (
                            <span className="gx-compact-amount__decimal gx-p14-b">
                              {formatted.decimal}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {slides.length > 1 && (
            <div className="gx-account-carousel__dots">
              {slides.map((slideAccounts, index) => (
                <button
                  key={slideAccounts.map((a) => a.id).join("-") || index}
                  type="button"
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={index === activeDotIndex}
                  className={`gx-account-carousel__dot${
                    index === activeDotIndex
                      ? " gx-account-carousel__dot--active"
                      : ""
                  }`}
                  onClick={() => goToSlide(index)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccountsWidget;
